/**
 * <uhs-countdown> — the PDP shipping cut-off timer.
 *
 * Ported from the live theme's implementation in
 * UHS-LiveTheme/snippets/product-variant.liquid (the `updateTimer` block), NOT
 * from a metafield. An earlier version of this element read
 * product.metafields.c_f.countdown_timer; that string does not appear anywhere
 * in the live theme, so it was the wrong source. The real rule is a fixed daily
 * warehouse cut-off.
 *
 * The rule, matching live:
 *   - Cut-off is 3:30 PM US Eastern.
 *   - Mon-Thu before cut-off  -> "Get it shipped Today! Order within: Xh Ym Zs"
 *   - Mon-Thu after cut-off   -> "Get it shipped Tomorrow! Order within: ..."
 *   - Fri after cut-off       -> "Get it shipped Monday! Order Now"  (no timer)
 *   - Saturday                -> "Get it shipped Monday! Order Now"  (no timer)
 *   - Sunday                  -> "Get it shipped Tomorrow! Order Now" (no timer)
 *
 * TWO BUGS IN THE LIVE VERSION ARE FIXED HERE, not carried forward:
 *
 * 1. Daylight saving. Live hardcodes the cut-off as `setUTCHours(19)`, i.e.
 *    19:30 UTC. That is 3:30 PM Eastern only during EDT; under EST it silently
 *    becomes 2:30 PM, so for roughly four months a year the site promises a
 *    cut-off an hour earlier than the warehouse actually keeps. The live file
 *    even carries the comment "needs to be adjusted for daylight savings time".
 *    Here the cut-off is resolved in America/New_York via Intl, so it is 3:30 PM
 *    Eastern all year regardless of the viewer's own timezone.
 *
 * 2. Mixed day basis. Live computes the next target with local `getDay()` but
 *    branches the message on `getUTCDay()`. For a viewer whose local day and UTC
 *    day differ, the words and the number disagree. Everything here derives from
 *    one Eastern-time basis.
 *
 * Markup contract (snippets/pdp-dropship-leadtime.liquid), three nodes:
 *   [data-countdown-headline]  "Get it shipped Tomorrow!"
 *   [data-countdown-within]    "Order within:"  (or "Order Now" at a weekend)
 *   .uhs-buy__ship-time        "22h 28m 54s EST"
 * Split this way because the design keeps all of it on one line on desktop but
 * breaks after the headline on mobile, and because only the last node is bold
 * red. All copy arrives as data-* attributes, so no English lives in here.
 *
 * Teardown matters: Dawn's assets/product-info.js swaps the innerHTML of
 * #price-{section.id} on every variant change, and this element sits inside
 * that box, so it is destroyed and recreated each time. The live version calls
 * `setInterval(updateTimer, 1000)` and never clears it. Here the interval is
 * cleared in disconnectedCallback, so clicking through variants cannot leave a
 * pile of timers running against detached nodes.
 */
if (!customElements.get('uhs-countdown')) {
  const TIMEZONE = 'America/New_York';
  const CUTOFF_HOUR = 15;
  const CUTOFF_MINUTE = 30;

  // Must match the token snippets/uhs-buy-price.liquid substitutes into the
  // data-label-* attributes. It is NOT `{{ day }}`: Liquid's tokenizer is not
  // string-aware, so a literal {{ day }} inside a Liquid tag closes the output
  // tag on the inner braces. These two sides drifted once already and rendered
  // the raw token on the storefront.
  const DAY_TOKEN = '__DAY__';

  class UhsCountdown extends HTMLElement {
    constructor() {
      super();
      this.intervalId = null;
    }

    connectedCallback() {
      this.headlineEl = this.querySelector('[data-countdown-headline]');
      this.withinEl = this.querySelector('[data-countdown-within]');
      this.timeEl = this.querySelector('.uhs-buy__ship-time');
      if (!this.headlineEl || !this.withinEl || !this.timeEl) return;

      this.copy = {
        today: this.dataset.labelToday || 'Today',
        tomorrow: this.dataset.labelTomorrow || 'Tomorrow',
        monday: this.dataset.labelMonday || 'Monday',
        headline: this.dataset.labelHeadline || `Get it shipped ${DAY_TOKEN}!`,
        within: this.dataset.labelWithin || 'Order within:',
        now: this.dataset.labelNow || 'Order Now',
        timezone: this.dataset.labelTimezone || '',
      };

      this.tick();
      this.intervalId = window.setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      if (this.intervalId !== null) {
        window.clearInterval(this.intervalId);
        this.intervalId = null;
      }
    }

    /**
     * The current time expressed in the warehouse's timezone, as plain parts.
     * Intl does the DST work, so this stays correct across the switch and is
     * independent of the viewer's own clock.
     */
    easternParts(date) {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: TIMEZONE,
        weekday: 'short',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        hour12: false,
      }).formatToParts(date);

      const get = (type) => parts.find((p) => p.type === type).value;
      const days = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

      return {
        day: days[get('weekday')],
        // Intl can emit hour 24 for midnight with hour12: false.
        hour: Number(get('hour')) % 24,
        minute: Number(get('minute')),
        second: Number(get('second')),
      };
    }

    /** Seconds remaining until today's cut-off. Negative once it has passed. */
    secondsUntilCutoff(now) {
      const cutoff = CUTOFF_HOUR * 3600 + CUTOFF_MINUTE * 60;
      const current = now.hour * 3600 + now.minute * 60 + now.second;
      return cutoff - current;
    }

    tick() {
      const now = this.easternParts(new Date());
      const remaining = this.secondsUntilCutoff(now);
      const beforeCutoff = remaining > 0;

      // Saturday, and Friday once the cut-off has passed, both ship Monday.
      // Sunday ships Monday too, which is "tomorrow" from Sunday.
      if (now.day === 6 || (now.day === 5 && !beforeCutoff)) {
        this.renderOrderNow(this.copy.monday);
        return;
      }
      if (now.day === 0) {
        this.renderOrderNow(this.copy.tomorrow);
        return;
      }

      // Mon-Fri. Before the cut-off it ships today; after, the next working day.
      // After Friday's cut-off is handled above, so "tomorrow" is always valid.
      const day = beforeCutoff ? this.copy.today : this.copy.tomorrow;
      const seconds = beforeCutoff ? remaining : remaining + 86400;

      this.headlineEl.textContent = this.copy.headline.replace(DAY_TOKEN, day);
      this.withinEl.textContent = this.copy.within;
      this.timeEl.textContent = this.withTimezone(this.format(seconds));
      this.timeEl.hidden = false;
    }

    /** Weekend and post-cut-off Friday: a day, but no countdown. */
    renderOrderNow(day) {
      this.headlineEl.textContent = this.copy.headline.replace(DAY_TOKEN, day);
      this.withinEl.textContent = this.copy.now;
      this.timeEl.textContent = '';
      this.timeEl.hidden = true;
    }

    /**
     * Appends the timezone label, matching live, whose setCountdown() returns
     * `${hours}h ${minutes}m ${seconds}s EST`.
     *
     * It is a fixed label there and a fixed translated string here, NOT the
     * viewer's zone and not derived from the clock — so it still reads "EST"
     * during daylight saving, when Eastern is really EDT. That inaccuracy is
     * live's and the design's (Figma 2256:7609 / 2406:6536 both show EST), and
     * it is reproduced deliberately rather than quietly corrected. The cut-off
     * ARITHMETIC above is a different matter and is correct year round.
     *
     * Swapping to a real abbreviation later is a one-liner: ask Intl for
     * timeZoneName: 'short' in TIMEZONE and use what it returns.
     */
    withTimezone(time) {
      return this.copy.timezone ? `${time} ${this.copy.timezone}` : time;
    }

    format(totalSeconds) {
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = Math.floor(totalSeconds % 60);
      const pad = (value) => String(value).padStart(2, '0');
      return `${hours}h ${pad(minutes)}m ${pad(seconds)}s`;
    }
  }

  customElements.define('uhs-countdown', UhsCountdown);
}
